import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Settings, Shield, BarChart3, Target, Puzzle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { logger } from '../../lib/logger';

/** Met à jour le Google Consent Mode v2 + Microsoft Clarity / UET selon les choix utilisateur */
function updateGoogleConsent(consent: { analytics: boolean; marketing: boolean; functional: boolean }) {
  if (typeof window === 'undefined') return;

  const applyConsentUpdate = () => {
    if (typeof window.gtag !== 'function') return;
    // Keep ads + analytics granted so GA4 → Google Ads conversions stay active
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      functionality_storage: consent.functional ? 'granted' : 'denied',
    });
  };
  applyConsentUpdate();

  // Always ensure GTM is loaded
  const w = window as Window & { __kombyLoadAnalytics?: () => void };
  w.__kombyLoadAnalytics?.();

  import('../../lib/clarity').then(({ setClarityConsent }) => {
    setClarityConsent(true);
  }).catch(() => { /* no-op */ });

  // Meta Pixel + Microsoft UET — always on
  import('../../lib/microsoftUet').then(({ loadMicrosoftUet }) => {
    loadMicrosoftUet();
  }).catch(() => { /* no-op */ });
  import('../../lib/metaPixel').then(({ loadMetaPixel }) => {
    loadMetaPixel();
  }).catch(() => { /* no-op */ });

  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
  });
}

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface CookieBannerProps {
  onConsentChange?: (consent: CookieConsent) => void;
}

interface GeoLocation {
  country: string;
  isEU: boolean;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ onConsentChange }) => {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth(); // Hook pour récupérer l'utilisateur connecté
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [geoData, setGeoData] = useState<GeoLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true, // Toujours true (cookies techniques)
    analytics: false,
    marketing: false,
    functional: false,
  });

  // Liste des pays UE/EEE (mise à jour 2025)
  const EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 
    'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 
    'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'CH'
  ];

  // Fonction pour vérifier si l'utilisateur est exclu de la bannière
  const isUserExcluded = (): boolean => {
    // Vérification si on est dans l'admin (URL contient /admin)
    if (window.location.pathname.includes('/admin')) {
      return true;
    }

    // Vérification si l'utilisateur est connecté et a un email @komby.io
    if (user?.email && user.email.endsWith('@komby.io')) {
      return true;
    }

    return false;
  };

  // Détection géographique via API avec cache
  const detectUserLocation = async (): Promise<GeoLocation> => {
    try {
      // En mode debug, ne pas appeler l'API tierce pour éviter du bruit réseau
      const params = new URLSearchParams(location.search);
      const isDebug = params.has('ga_debug') || localStorage.getItem('ga_debug') === '1';
      if (isDebug) {
        return { country: 'FR', isEU: true };
      }

      // Vérifier cache géolocalisation (24h)
      const cacheKey = 'komby_geo_cache';
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000; // 24h
          if (!isExpired && data.country && typeof data.isEU === 'boolean') {
            return data;
          }
        } catch (e) {
          // Cache corrompu, continuer avec API
        }
      }

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      // Tentative CORS tolérante
      const response = await fetch('https://ipapi.co/json/', { signal: controller.signal, mode: 'cors' as RequestMode });
      clearTimeout(timeout);
      if (!response.ok) throw new Error(`ipapi status ${response.status}`);
      const data = await response.json().catch(() => ({}));
      const country = (data.country_code || 'FR').toUpperCase();
      const isEU = EU_COUNTRIES.includes(country);
      const result = { country, isEU };
      
      // Sauvegarder en cache
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: result,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore cache errors
      }
      
      return result;
    } catch (error) {
      logger.warn('Geo detection failed, using safe fallback (EU):', error);
      return { country: 'FR', isEU: true };
    }
  };

  // Vérification du consentement existant
  const checkExistingConsent = (): boolean => {
    try {
      const stored = localStorage.getItem('komby_cookie_consent');
      if (!stored) return false;
      
      const data = JSON.parse(stored);
      const expiryDate = new Date(data.expiresAt);
      
      if (expiryDate > new Date()) {
        setConsent(data.consent);
        // Restaurer le consentement GA4 au chargement
        updateGoogleConsent(data.consent);
        return true;
      } else {
        // Consentement expiré
        localStorage.removeItem('komby_cookie_consent');
        return false;
      }
    } catch (error) {
      logger.warn('Erreur lecture consentement:', error);
      return false;
    }
  };

  // Sauvegarde du consentement
  const saveConsent = async (newConsent: CookieConsent, geoOverride?: GeoLocation) => {
    const geo = geoOverride || geoData;
    const consentData = {
      consent: newConsent,
      timestamp: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 12 mois
      country: geo?.country,
      isEU: geo?.isEU,
      version: '1.0'
    };

    // Stockage local
    localStorage.setItem('komby_cookie_consent', JSON.stringify(consentData));
    // Session ID persistant pour retrouver le consentement côté serveur
    let sessionId = localStorage.getItem('komby_session_id');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('komby_session_id', sessionId);
    }

    // Envoi vers Supabase — hors chemin LCP (idle / keepalive)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const payload = JSON.stringify({
        session_id: sessionId,
        user_id: user?.id || null,
        necessary_cookies: true,
        analytics_cookies: !!newConsent.analytics,
        marketing_cookies: !!newConsent.marketing,
        functional_cookies: !!newConsent.functional,
        country_code: geo?.country,
        is_eu_user: !!geo?.isEU
      });
      const post = () => {
        void fetch(`${supabaseUrl}/functions/v1/save-cookie-consent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anon}`
          },
          body: payload,
          keepalive: true,
        }).catch(() => { /* no-op */ });
      };
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(post, { timeout: 8000 });
      } else {
        setTimeout(post, 3000);
      }
    } catch (error) {
      logger.warn('Erreur sauvegarde consentement serveur:', error);
    }

    onConsentChange?.(newConsent);
  };

  // Initialisation — différer geo + save-cookie-consent hors Lighthouse window
  useEffect(() => {
    let cancelled = false;

    const initializeBanner = async () => {
      // Admin / @komby.io : pas de bannière, analytics actif pour le debug
      if (isUserExcluded()) {
        logger.log('Utilisateur exclu de la bannière RGPD (admin ou @komby.io)');
        updateGoogleConsent({ analytics: true, marketing: false, functional: true });
        setIsVisible(false);
        setIsLoading(false);
        return;
      }

      // Consent déjà stocké → pas de réseau (ipapi / save-cookie-consent)
      if (checkExistingConsent()) {
        if (!cancelled) {
          setIsVisible(false);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      const location = await detectUserLocation();
      if (cancelled) return;
      setGeoData(location);

      if (!location.isEU) {
        const nonEuConsent: CookieConsent = {
          necessary: true,
          analytics: true,
          marketing: false,
          functional: true,
        };
        setConsent(nonEuConsent);
        // saveConsent is idle-deferred (CWV)
        void saveConsent(nonEuConsent, location);
        updateGoogleConsent(nonEuConsent);
        setIsVisible(false);
        setIsLoading(false);
        return;
      }

      setIsVisible(true);
      setIsLoading(false);
    };

    const start = () => {
      void initializeBanner();
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    // Delay geo/banner until after first paint / LCP window on landings
    let idleId: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      idleId = w.requestIdleCallback(start, { timeout: 6000 });
    } else {
      timer = setTimeout(start, 2500);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback === 'function') {
        (window as Window & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleId);
      }
      if (timer) clearTimeout(timer);
    };
  }, [user]); // Dépendance sur user pour re-vérifier quand l'utilisateur se connecte

  // Gestion des actions
  const handleAcceptAll = async () => {
    const fullConsent: CookieConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };

    // Paint hide first — defer consent/pixels off the interaction path (INP)
    setIsVisible(false);
    const apply = () => {
      void saveConsent(fullConsent);
      updateGoogleConsent(fullConsent);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(apply, { timeout: 800 });
    } else {
      setTimeout(apply, 0);
    }
  };

  const handleRejectAll = async () => {
    const minimalConsent: CookieConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };

    setIsVisible(false);
    const apply = () => {
      void saveConsent(minimalConsent);
      updateGoogleConsent(minimalConsent);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(apply, { timeout: 800 });
    } else {
      setTimeout(apply, 0);
    }
  };

  const handleSaveSettings = async () => {
    setIsVisible(false);
    setShowSettings(false);
    const apply = () => {
      void saveConsent(consent);
      updateGoogleConsent(consent);
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(apply, { timeout: 800 });
    } else {
      setTimeout(apply, 0);
    }
  };

  const toggleCategory = (category: keyof Omit<CookieConsent, 'necessary'>) => {
    setConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Pas d'affichage si pas visible, en cours de chargement, ou utilisateur exclu
  if (!isVisible || isLoading || isUserExcluded()) return null;

  // Catégories de cookies avec icônes
  const cookieCategories = [
    {
      key: 'analytics' as const,
      icon: BarChart3,
      title: t('cookies.analytics.title', 'Cookies analytiques'),
      description: t('cookies.analytics.description', 'Nous aident à comprendre comment vous utilisez notre site'),
      required: false
    },
    {
      key: 'marketing' as const,
      icon: Target,
      title: t('cookies.marketing.title', 'Cookies marketing'),
      description: t('cookies.marketing.description', 'Permettent de personnaliser les publicités'),
      required: false
    },
    {
      key: 'functional' as const,
      icon: Puzzle,
      title: t('cookies.functional.title', 'Cookies fonctionnels'),
      description: t('cookies.functional.description', 'Améliorent votre expérience utilisateur'),
      required: false
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-t-4 border-yellow-400 shadow-2xl">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {!showSettings ? (
          // Vue principale
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 sm:mb-3">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {t('cookies.title', 'Respect de votre vie privée')}
                </h3>
              </div>
              
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 leading-relaxed">
                {t('cookies.description', 
                  'Nous utilisons des cookies pour améliorer votre expérience, analyser notre trafic et personnaliser le contenu. Vous pouvez accepter tous les cookies ou choisir vos préférences.'
                )}
              </p>
              
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                {t('cookies.dataInfo', 'Données collectées : adresse IP, comportement de navigation, préférences linguistiques')}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 min-w-fit">
              <button
                onClick={handleRejectAll}
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {t('cookies.rejectAll', 'Refuser tout')}
              </button>
              
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">{t('cookies.customize', 'Personnaliser')}</span>
                <span className="sm:hidden">{t('cookies.settings', 'Paramètres')}</span>
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
              >
                {t('cookies.acceptAll', 'Accepter tout')}
              </button>
            </div>
          </div>
        ) : (
          // Vue paramètres détaillés
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Settings className="h-6 w-6 text-yellow-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('cookies.settingsTitle', 'Paramètres des cookies')}
                </h3>
              </div>
              
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Cookies nécessaires */}
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t('cookies.necessary.title', 'Cookies nécessaires')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('cookies.necessary.description', 'Essentiels au fonctionnement du site (sécurité, authentification)')}
                    </p>
                  </div>
                </div>
                <div className="text-green-600 font-medium">
                  {t('cookies.alwaysActive', 'Toujours actif')}
                </div>
              </div>
            </div>

            {/* Catégories optionnelles */}
            <div className="space-y-4 mb-6">
              {cookieCategories.map(({ key, icon: Icon, title, description }) => (
                <div key={key} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
                      </div>
                    </div>
                    
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consent[key]}
                        onChange={() => toggleCategory(key)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
              <button
                onClick={handleRejectAll}
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {t('cookies.rejectAll', 'Refuser tout')}
              </button>
              
              <button
                onClick={handleSaveSettings}
                className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
              >
                {t('cookies.saveSettings', 'Sauvegarder les préférences')}
              </button>
            </div>

            {/* Informations légales */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <p>
                {t('cookies.legalInfo', 
                  'Conformément au RGPD, vous pouvez modifier vos préférences à tout moment. Les consentements sont conservés 12 mois maximum.'
                )}
              </p>
              <p className="mt-2">
                {t('cookies.withdrawInfo', 'Pour retirer votre consentement : ')}
                <button 
                  onClick={handleRejectAll}
                  className="text-yellow-600 hover:text-yellow-700 underline"
                >
                  {t('cookies.clickHere', 'cliquez ici')}
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner; 